/**
 * Shared count-up/down primitive, originally built for components/search-panel/motion.ts (round
 * 9) and moved here (round 10) once the navbar live-index figures (IndexStrip.tsx,
 * MobileIndexSubtitle.tsx, and the static twin's own vanilla equivalent in
 * landing-nav-live-index-script.ts) needed the identical behaviour: animate a displayed number
 * toward a new value over a fixed duration, easing with {@link EASE}, respecting
 * `prefers-reduced-motion`. components/search-panel/motion.ts re-exports everything here so
 * SearchPanel.tsx's own imports are unaffected by the move.
 */
import { useEffect, useRef, useState } from 'react';

/** The one easing curve every motion effect in this app uses: a fast start that settles gently. */
export const EASE = 'cubic-bezier(0.2, 0.8, 0.2, 1)';

/** `prefers-reduced-motion: reduce` check, safe to call outside a component (module scope, SSR: no
 * `window`) and cheap enough to call on every render rather than caching it at import time, since a
 * visitor can flip this OS setting while the page is open. */
export function isReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/** React hook form of {@link isReducedMotion}, kept in sync if the visitor changes the OS setting
 * while the page is open (rather than only reading it once on mount). */
export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(isReducedMotion);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduced(mql.matches);
    if (typeof mql.addEventListener === 'function') mql.addEventListener('change', onChange);
    else if (typeof (mql as unknown as { addListener?: (cb: () => void) => void }).addListener === 'function') {
      (mql as unknown as { addListener: (cb: () => void) => void }).addListener(onChange);
    }
    return () => {
      if (typeof mql.removeEventListener === 'function') mql.removeEventListener('change', onChange);
      else if (typeof (mql as unknown as { removeListener?: (cb: () => void) => void }).removeListener === 'function') {
        (mql as unknown as { removeListener: (cb: () => void) => void }).removeListener(onChange);
      }
    };
  }, []);
  return reduced;
}

/** A close, allocation-free approximation of the `EASE` cubic-bezier's shape for per-frame
 * interpolation (a true cubic-bezier solve needs iterative root-finding for a handful of animated
 * numbers per keystroke/pulse-refresh; this is visually indistinguishable and far cheaper). Fast
 * start, gentle settle, no overshoot, just like `EASE` itself. */
function easeOutCubicBezierApprox(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

export interface RecountOnMountOptions {
  /** Whether THIS mount should recount at all (the caller decides "first view this session",
   * shared across every figure animating together, not decided independently per hook instance:
   * see utils/motion/nav-figure-recount.ts). */
  enabled: boolean;
  /** Start the mount animation from this fraction of the real value (0.9 per the navbar figures'
   * own spec: "no visible drop to zero, so it reads as a live recount rather than a reload"). */
  fromRatio: number;
  duration: number;
  /** Staggers this hook's own animation start relative to its siblings (e.g. index * 80ms across
   * four navbar figures). */
  delayMs?: number;
}

export interface UseCountUpOptions {
  duration?: number;
  disabled?: boolean;
  /** Round 10 (navbar live-index figures): animates from `fromRatio * value` up to `value` on
   * this hook's very first render, instead of the default "show the value immediately, animate
   * only on later changes." Omitted (the default) for every other caller (SearchPanel's own
   * counts): a value that already appears in server-rendered/committed markup must never animate
   * away from itself. */
  recountOnMount?: RecountOnMountOptions;
}

/**
 * Count-up/down: animates the displayed number from its previous value to `value` over
 * `options.duration` (default 250ms), easing with {@link EASE}, using `requestAnimationFrame` (no
 * library, no re-render-per-frame timers). Under reduced motion, when disabled, or on an ordinary
 * first render (no `recountOnMount`), the value is shown immediately: nothing animates away from
 * a number that's already visible. Callers are expected to render the result with `tabular-nums`
 * themselves; this hook only owns the number, not its markup.
 */
export function useCountUp(value: number, options?: UseCountUpOptions): number {
  const duration = options?.duration ?? 250;
  const reducedMotion = useReducedMotion();
  const disabled = options?.disabled ?? false;
  const recount = options?.recountOnMount;
  const [displayed, setDisplayed] = useState(() => (recount?.enabled && !isReducedMotion() ? Math.round(value * recount.fromRatio) : value));
  const fromRef = useRef(displayed);
  const rafRef = useRef<number | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFirstRenderRef = useRef(true);

  useEffect(() => {
    const runAnimation = (from: number, to: number, animDuration: number, delayMs: number) => {
      if (from === to) {
        fromRef.current = to;
        return;
      }
      const start = () => {
        const startTime = performance.now();
        const tick = (now: number) => {
          const elapsed = now - startTime;
          const t = Math.min(1, elapsed / animDuration);
          const eased = easeOutCubicBezierApprox(t);
          const next = Math.round(from + (to - from) * eased);
          setDisplayed(next);
          if (t < 1) {
            rafRef.current = requestAnimationFrame(tick);
          } else {
            fromRef.current = to;
            rafRef.current = null;
          }
        };
        rafRef.current = requestAnimationFrame(tick);
      };
      if (delayMs > 0) {
        timeoutRef.current = setTimeout(start, delayMs);
      } else {
        start();
      }
    };

    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      if (recount?.enabled && !reducedMotion && !disabled && typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
        // Recount-on-mount: already initialized to `value * fromRatio` in useState's own
        // initializer above (so the very first paint shows that starting point, not a flash of
        // the real value followed by a jump backward), now animate from there up to `value`.
        runAnimation(Math.round(value * recount.fromRatio), value, recount.duration, recount.delayMs ?? 0);
      } else {
        fromRef.current = value;
        setDisplayed(value);
      }
      return () => {
        if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
        if (timeoutRef.current !== null) clearTimeout(timeoutRef.current);
      };
    }

    if (reducedMotion || disabled || typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
      fromRef.current = value;
      setDisplayed(value);
      return;
    }
    const from = fromRef.current;
    if (from === value) return;
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    if (timeoutRef.current !== null) clearTimeout(timeoutRef.current);
    runAnimation(from, value, duration, 0);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      if (timeoutRef.current !== null) clearTimeout(timeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fromRef/recount/duration are intentionally not deps: fromRef is a ref, recount only matters on the first render, and duration changing mid-animation is not a case any caller hits.
  }, [value, reducedMotion, disabled]);

  return displayed;
}
